<script setup lang="ts">
import { createErrorMessage } from '@errors/helpers';
import { getSurenetUrl } from '@menu-lib/urlHelper';
import { type AanvragenPolissenGetPakketPolissenByRelatiePakketPolisDto as PakketpolisDto } from '@services/polis-public/orval-api/model/aanvragenPolissenGetPakketPolissenByRelatiePakketPolisDto';
import { usePolisGetPolissen } from '@services/polis-public/orval-api/polis-api/polis-api';
import VCallToAction from '@shared/component/VCallToAction/VCallToAction.vue';
import VContentBox from '@shared/component/VContentBox/VContentBox.vue';
import VInfoBox from '@shared/component/VInfoBox/VInfoBox.vue';
import VSkeleton from '@shared/component/VSkeleton/VSkeleton.vue';
import VPageContainer from '@shared/layout/VPageContainer/VPageContainer.vue';
import { computed, reactive, toRefs } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { type MutatieGestart, PakketpolisContentBox } from '../../Polissen';

type PakketpolisItem = PakketpolisDto & { isOpen: boolean };

const props = defineProps<{
  relatienummer: number;
}>();

const { relatienummer } = toRefs(props);

const route = useRoute();
const router = useRouter();

const {
  isPending,
  isError,
  error,
  data: pakketpolissen,
} = usePolisGetPolissen(
  computed(() => ({
    relatienummer: relatienummer.value,
    neemRoyementenMee: true,
    neemBeursPakkettenMee: true,
  })),
  {
    query: {
      select: (result) => result.result,
      retry: 3,
      staleTime: 5 * 60 * 1000,
    },
  },
);

const pakketpolisItems = computed(() => {
  if (!pakketpolissen.value) return undefined;

  const actievePakketten: PakketpolisItem[] = pakketpolissen.value
    .filter((x) => !x.isGeroyeerd)
    .map((x) => reactive({ ...x, isOpen: false }));

  const geroyeerdePakketten: PakketpolisItem[] = pakketpolissen.value
    .filter((x) => x.isGeroyeerd)
    .map((x) => reactive({ ...x, isOpen: false }));

  if (actievePakketten.length === 1) {
    actievePakketten[0].isOpen = true;
  } else if (geroyeerdePakketten.length === 1 && actievePakketten.length === 0) {
    geroyeerdePakketten[0].isOpen = true;
  }

  const polissen = actievePakketten.concat(geroyeerdePakketten);

  if (route.hash) {
    const hashParams = new URLSearchParams(route.hash.substring(1));
    const pakketpolissenToOpen = hashParams.get('open')?.split(',');
    if (pakketpolissenToOpen && pakketpolissenToOpen.length > 0) {
      return polissen.map((x) =>
        reactive({ ...x, isOpen: pakketpolissenToOpen.includes(x.polisnummer) }),
      );
    }
  }

  return polissen;
});

function onMutatieGestart(event: MutatieGestart) {
  if (event.wagenparkId && event.offerteproductId) {
    window.location.href = `/app/offerte/offertes/${event.offertenummer}/${event.offerteproductId}/wagenpark/${event.wagenparkId}`;
    return;
  }

  if (event.directTeVerwerken) {
    window.location.href = getSurenetUrl(
      `/content/boaz/offerte/verwerken?offertenummer=${event.offertenummer}`,
      false,
    );
  } else {
    window.location.href = `/app/offerte/offertes/${event.offertenummer}/premievragen/${event.offerteproductId}`;
  }
}

function onPakketOpenedOrClosed() {
  if (!pakketpolisItems.value) return;

  const openedPakketpolissen = pakketpolisItems.value
    .filter((x) => x.isOpen)
    .map((x) => x.polisnummer)
    .join(',');

  router.replace({ hash: `#open=${openedPakketpolissen}` });
}
</script>

<template>
  <VPageContainer container-size="sm" title="Polissen">
    <VInfoBox badge-text="error" type="danger" v-if="isError">
      <div v-html="createErrorMessage(error)"></div>
    </VInfoBox>
    <VSkeleton v-else-if="isPending || !pakketpolisItems" height="xl" />
    <div v-else class="space-y-4">
      <PakketpolisContentBox
        v-for="polis in pakketpolisItems"
        :key="polis.polisnummer"
        @mutatie-gestart="onMutatieGestart"
        :relatienummer="relatienummer"
        :polis="polis"
        v-model:open="polis.isOpen"
        @update:open="onPakketOpenedOrClosed"
      />
    </div>
    <VContentBox v-if="pakketpolissen?.length === 0">
      <VCallToAction
        class="py-16"
        title="Deze relatie heeft geen polissen."
        icon="far fa-check-double"
      />
    </VContentBox>
  </VPageContainer>
</template>
